import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMonetization } from '../contexts/MonetizationContext';
import DisButton from '../components/design-system/DisButton';
import DisAvatar from '../components/design-system/DisAvatar';
import AddChildModal from '../components/AddChildModal';
import FloatingMenu from '../components/FloatingMenu';
import Footer from '../components/Footer';

const ChildrenPage = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { currentPlan, isPremium, isFamily } = useMonetization();
  const [children, setChildren] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', birthMonth: '', birthYear: '', gender: 'neutral' });
  const [editErrors, setEditErrors] = useState({});

  // Couleurs pour les cartes d'enfants - Style app mobile
  const childCardGradients = [
    { from: '#4A90D9', to: '#5BBFBD' }, // pair
    { from: '#4ECDC4', to: '#45B7AA' }, // impair
  ];

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await api.get('/children');
      setChildren(response.data.children || []);
    } catch (error) {
      console.error('Erreur lors du chargement des enfants:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddChild = async (childData) => {
    const response = await api.post('/children', childData);
    if (response.data.child) {
      setChildren(prev => [...prev, response.data.child]);
    }
    setShowAddChildModal(false);
  };

  const handleEditClick = (child) => {
    setEditingChild(child);
    setEditForm({
      name: child.name,
      birthMonth: child.birth_month?.toString() || '',
      birthYear: child.birth_year?.toString() || '',
      gender: child.gender || 'neutral'
    });
    setEditErrors({});
  };

  const handleEditCancel = () => {
    setEditingChild(null);
    setEditForm({ name: '', birthMonth: '', birthYear: '', gender: 'neutral' });
    setEditErrors({});
  };

  const calculateAge = (birthMonth, birthYear) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    let age = currentYear - parseInt(birthYear);
    if (currentMonth < parseInt(birthMonth)) {
      age--;
    }
    return age;
  };

  const validateEditForm = () => {
    const newErrors = {};
    if (!editForm.name.trim()) {
      newErrors.name = 'Le prénom est requis';
    }
    if (!editForm.birthMonth) {
      newErrors.birthMonth = 'Sélectionnez un mois';
    }
    if (!editForm.birthYear) {
      newErrors.birthYear = 'Sélectionnez une année';
    }
    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditSave = async () => {
    if (!validateEditForm()) return;

    setIsSaving(true);
    try {
      const calculatedAge = calculateAge(editForm.birthMonth, editForm.birthYear);
      const response = await api.put(`/children/${editingChild.id}`, {
        name: editForm.name.trim(),
        age: calculatedAge,
        birth_month: parseInt(editForm.birthMonth),
        birth_year: parseInt(editForm.birthYear),
        gender: editForm.gender
      });
      
      if (response.data.child) {
        setChildren(prev => prev.map(c => c.id === editingChild.id ? response.data.child : c));
      }
      setEditingChild(null);
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la modification';
      setEditErrors({ submit: errorMessage });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (child) => {
    setShowDeleteConfirm(child);
  };

  const handleConfirmDelete = async () => {
    if (!showDeleteConfirm) return;
    
    setIsDeleting(true);
    try {
      await api.delete(`/children/${showDeleteConfirm.id}`);
      setChildren(prev => prev.filter(c => c.id !== showDeleteConfirm.id));
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const months = [
    { value: '1', label: 'Janvier' }, { value: '2', label: 'Février' },
    { value: '3', label: 'Mars' }, { value: '4', label: 'Avril' },
    { value: '5', label: 'Mai' }, { value: '6', label: 'Juin' },
    { value: '7', label: 'Juillet' }, { value: '8', label: 'Août' },
    { value: '9', label: 'Septembre' }, { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' }, { value: '12', label: 'Décembre' }
  ];

  return (
    <>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');`}
      </style>
      
      <div className="min-h-screen relative overflow-hidden pb-20" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
        
        {/* Bulle de texte style BD */}
        <div className="absolute left-4 sm:left-8 top-4 sm:top-8 z-20">
          <div className="relative">
            <svg viewBox="0 0 220 90" className="w-[140px] sm:w-[180px] h-auto" style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' }}>
              <path d="M 20,45 C 20,20 40,8 80,8 C 120,5 160,10 190,20 C 210,28 215,45 210,60 C 205,75 180,82 140,85 C 100,88 60,85 35,75 L 15,88 L 25,70 C 18,65 20,55 20,45 Z" fill="white" stroke="#374151" strokeWidth="3"/>
            </svg>
            <p className="absolute top-1/2 left-1/2 text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 whitespace-nowrap" style={{ fontFamily: '"Caveat", cursive', transform: 'translate(-52%, -45%) rotate(-1deg)' }}>
              Dis Maman !
            </p>
          </div>
        </div>

        {/* Mascotte avec cœur */}
        <div className="absolute right-0 top-0 z-0 pointer-events-none">
          <img 
            src="https://customer-assets.emergentagent.com/job_kidchat-5/artifacts/9s3fmsx8_image.png"
            alt="Mascotte"
            className="h-[200px] sm:h-[320px] md:h-[400px] lg:h-[480px] object-contain opacity-80 sm:opacity-90"
            style={{ filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.2))' }}
          />
        </div>

        {/* Contenu principal */}
        <div className="relative z-10 max-w-2xl mx-auto px-4 pt-32 sm:pt-36">
          
          {/* Header */}
          <div className="text-center mb-8">
            <div className="text-5xl mb-3">👶</div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: '"Caveat", cursive', textShadow: '0 2px 4px rgba(255,255,255,0.5)' }}>
              Mes enfants
            </h1>
            <p className="text-gray-600" style={{ textShadow: '0 1px 2px rgba(255,255,255,0.5)' }}>
              Gérez les profils de vos enfants
            </p>
          </div>

          {/* Loading */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="text-4xl animate-bounce">🔄</div>
              <p className="text-gray-600 mt-2">Chargement...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Liste des enfants */}
              {children.map((child, index) => {
                const gradient = childCardGradients[index % 2];
                const isEditing = editingChild?.id === child.id;

                return (
                  <div 
                    key={child.id} 
                    className="rounded-2xl p-5 shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${gradient.from} 0%, ${gradient.to} 100%)` }}
                  >
                    {isEditing ? (
                      // Mode édition - Style épuré
                      <div className="space-y-4">
                        <h3 
                          className="text-white font-bold text-xl mb-4"
                          style={{ fontFamily: '"Caveat", cursive' }}
                        >
                          Modifier le profil
                        </h3>

                        {/* Prénom */}
                        <div>
                          <label className="block text-white/90 text-sm font-medium mb-1">Prénom</label>
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                            className={`w-full px-4 py-2 rounded-xl border-2 ${editErrors.name ? 'border-red-300 bg-red-50' : 'border-white/30 bg-white/90'} focus:outline-none`}
                          />
                          {editErrors.name && <p className="text-red-200 text-xs mt-1">{editErrors.name}</p>}
                        </div>

                        {/* Date de naissance */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-white/90 text-sm font-medium mb-1">Mois</label>
                            <select
                              value={editForm.birthMonth}
                              onChange={(e) => setEditForm(prev => ({ ...prev, birthMonth: e.target.value }))}
                              className={`w-full px-3 py-2 rounded-xl border-2 ${editErrors.birthMonth ? 'border-red-300 bg-red-50' : 'border-white/30 bg-white/90'} focus:outline-none`}
                            >
                              <option value="">Mois</option>
                              {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-white/90 text-sm font-medium mb-1">Année</label>
                            <select
                              value={editForm.birthYear}
                              onChange={(e) => setEditForm(prev => ({ ...prev, birthYear: e.target.value }))}
                              className={`w-full px-3 py-2 rounded-xl border-2 ${editErrors.birthYear ? 'border-red-300 bg-red-50' : 'border-white/30 bg-white/90'} focus:outline-none`}
                            >
                              <option value="">Année</option>
                              {Array.from({ length: 18 }, (_, i) => new Date().getFullYear() - i - 1).map(year => (
                                <option key={year} value={year}>{year}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Genre */}
                        <div>
                          <label className="block text-white/90 text-sm font-medium mb-2">Genre</label>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { value: 'girl', emoji: '👧', label: 'Fille' },
                              { value: 'boy', emoji: '👦', label: 'Garçon' },
                              { value: 'neutral', emoji: '😊', label: 'Neutre' }
                            ].map(option => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => setEditForm(prev => ({ ...prev, gender: option.value }))}
                                className={`p-2 rounded-xl text-center transition-all ${
                                  editForm.gender === option.value
                                    ? 'bg-white shadow-md scale-105'
                                    : 'bg-white/40 hover:bg-white/60'
                                }`}
                              >
                                <div className="text-xl">{option.emoji}</div>
                                <span className="text-xs font-medium text-gray-700">{option.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {editErrors.submit && (
                          <div className="bg-red-100 border border-red-300 text-red-700 px-3 py-2 rounded-xl text-sm">
                            {editErrors.submit}
                          </div>
                        )}

                        {/* Boutons édition */}
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={handleEditCancel}
                            disabled={isSaving}
                            className="flex-1 px-4 py-2 rounded-xl bg-white/30 text-white font-medium hover:bg-white/40 transition-all"
                          >
                            Annuler
                          </button>
                          <button
                            onClick={handleEditSave}
                            disabled={isSaving}
                            className="flex-1 px-4 py-2 rounded-xl bg-white text-gray-800 font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                          >
                            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Mode affichage - Style épuré
                      <>
                        <div className="text-white">
                          <h3 
                            className="font-bold text-2xl mb-1"
                            style={{ fontFamily: '"Caveat", cursive' }}
                          >
                            {child.name}
                          </h3>
                          <p className="text-white/80 text-sm">
                            {child.age === 1 ? `${child.age} an` : `${child.age} ans`}
                          </p>
                        </div>

                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={() => navigate(`/badges/${child.id}`)}
                            className="flex-1 px-4 py-2 rounded-full text-white font-medium transition-all hover:opacity-90"
                            style={{ background: 'rgba(255, 200, 50, 0.35)' }}
                            data-testid={`child-badges-${child.id}`}
                          >
                            &#11088; Badges
                          </button>
                          <button
                            onClick={() => handleEditClick(child)}
                            className="flex-1 px-4 py-2 rounded-full text-white font-medium transition-all hover:opacity-90"
                            style={{ background: 'rgba(255, 255, 255, 0.25)' }}
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteClick(child)}
                            className="px-4 py-2 rounded-full text-white/80 font-medium transition-all hover:bg-red-500/50 hover:text-white"
                            style={{ background: 'rgba(255, 255, 255, 0.15)' }}
                          >
                            Supprimer
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              {/* Message si pas d'enfants */}
              {children.length === 0 && (
                <div className="text-center py-12 bg-white/60 backdrop-blur-sm rounded-2xl">
                  <h3 
                    className="text-xl font-bold text-gray-700 mb-2"
                    style={{ fontFamily: '"Caveat", cursive' }}
                  >
                    Aucun profil enfant
                  </h3>
                  <p className="text-gray-600 text-sm">Créez votre premier profil pour commencer !</p>
                </div>
              )}

              {/* Bouton ajouter - Style app mobile */}
              {children.length < 4 && (
                <button
                  onClick={() => setShowAddChildModal(true)}
                  className="w-full p-5 rounded-2xl border-2 border-dashed transition-all hover:opacity-90"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.8)',
                    borderColor: 'rgba(91, 191, 189, 0.3)'
                  }}
                >
                  <div className="text-center">
                    <span 
                      className="text-xl font-bold"
                      style={{ fontFamily: '"Caveat", cursive', color: '#4A90A4' }}
                    >
                      + Ajouter un enfant
                    </span>
                    <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>Jusqu'à 4 profils</p>
                  </div>
                </button>
              )}

              {/* Bannière upgrade Explorateur → Famille */}
              {isPremium && !isFamily && currentPlan?.id?.includes('essential') && children.length >= 1 && (
                <div 
                  data-testid="upgrade-family-banner"
                  className="rounded-2xl p-5"
                  style={{ 
                    background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
                    border: '1px solid #C7D2FE'
                  }}
                >
                  <p className="text-gray-800 font-medium leading-relaxed mb-3" style={{ fontSize: '0.95rem' }}>
                    {children.length > 0 
                      ? `${children[0].name} n'est pas le seul à poser des questions ? Avec le plan Famille, chaque enfant a son propre univers personnalisé`
                      : `Vous avez plusieurs enfants curieux ? Avec le plan Famille, chacun a son propre univers personnalisé`
                    } 🌈
                  </p>
                  <button
                    onClick={() => navigate('/subscription')}
                    data-testid="upgrade-family-cta"
                    className="w-full py-3 rounded-full font-bold text-white text-base transition-all hover:opacity-90 active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                  >
                    Découvrir le plan Famille
                  </button>
                </div>
              )}

              {/* Texte de réassurance - Style app mobile */}
              <div className="text-center mt-6">
                <p className="text-sm" style={{ color: '#9CA3AF' }}>
                  Les données de vos enfants sont sécurisées et ne sont jamais partagées
                </p>
              </div>

              {/* Message limite atteinte */}
              {children.length >= 4 && (
                <div className="text-center p-4 bg-white/60 backdrop-blur-sm rounded-2xl">
                  <p className="text-teal-700 font-medium">🎉 Maximum 4 profils enfants atteint !</p>
                  <p className="text-teal-600 text-sm">Supprimez un profil pour en ajouter un nouveau</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal de confirmation de suppression */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="text-center">
                <div className="text-5xl mb-4">🗑️</div>
                <h3 className="text-xl font-bold text-red-600 mb-4">
                  Supprimer le profil de {showDeleteConfirm.name} ?
                </h3>
                
                <div className="bg-red-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-center mb-3">
                    <DisAvatar name={showDeleteConfirm.name} gender={showDeleteConfirm.gender} size="lg" className="border-2 border-red-300" />
                  </div>
                  <p className="text-red-700 font-medium">
                    {showDeleteConfirm.name} • {showDeleteConfirm.age} ans
                  </p>
                </div>
                
                <div className="bg-yellow-50 rounded-xl p-3 mb-6 text-left">
                  <p className="text-yellow-800 text-sm font-medium">⚠️ Cette action est irréversible !</p>
                  <p className="text-yellow-700 text-xs mt-1">
                    • Tout l'historique des conversations sera perdu<br/>
                    • Les paramètres personnalisés seront effacés
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    disabled={isDeleting}
                    className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-all"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                    className="flex-1 px-4 py-3 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 transition-all disabled:opacity-50"
                  >
                    {isDeleting ? 'Suppression...' : 'Supprimer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Floating Menu */}
        <FloatingMenu 
          onNavigateToHistory={() => navigate('/history')}
          onNavigateToChildren={() => {}}
          onNavigateToSubscription={() => navigate('/subscription')}
          onNavigateToChat={() => navigate('/chat')}
          onNavigateToStory={() => navigate('/story')}
          currentPage="children"
        />

        <Footer />
      </div>

      {/* Modal d'ajout */}
      <AddChildModal
        isOpen={showAddChildModal}
        onClose={() => setShowAddChildModal(false)}
        onAddChild={handleAddChild}
        onUpgrade={() => navigate('/subscription')}
      />
    </>
  );
};

export default ChildrenPage;
